import React, {useEffect, useMemo, useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalCounterpartFields from 'chemistry/molecules/ChemicalCounterpartFields';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import * as MoleculeDAO from 'chemistry/molecules/MoleculeDAO';
import SchemaMismatchAlert from 'chemistry/molecules/SchemaMismatchAlert';
import useSchemaMismatchDetection, {MismatchStatus} from 'chemistry/molecules/useSchemaMismatchDetection';
import {MoleculeSchemaInterfaceApiId} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FileStates from 'FileStates';
import {SchemaTag} from 'folder-items/FileConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import CreateBioentityTagInputs, {
  useCreateBioentityTagInputs,
} from 'registry/create-bioentity/CreateBioentityTagInputs';
import {
  create as createBioentity,
  CreateBioentityArgs,
  getDefaultCreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {getLinksKeyFromFieldType, relevantKeyFromFieldType} from 'schemas/tags/TagUtil';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {stripSaltsAndNotify} from './MoleculeUtil';

const CHEMICAL_FORMAT_TO_MARVIN: Record<ChemicalFormatType, MarvinChemicalFormat> = {
  [ChemicalFormatType.SMILES]: MarvinChemicalFormat.SMILES,
  [ChemicalFormatType.MOLFILE]: MarvinChemicalFormat.MOL,
};

interface CreateMoleculeModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  initialChemicalStructure?: {value: string; format: ChemicalFormatType};
  onMoleculeCreated?: (moleculeId: string, moleculeName: string) => void;
}

export default function CreateMoleculeModal({
  initialFolderId,
  onRequestClose,
  onRequestSubmit,
  initialChemicalStructure,
  onMoleculeCreated,
}: CreateMoleculeModalProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [posting, setPosting] = useState(false);
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  const [hasBlockingError, setHasBlockingError] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [hasCounterparts, setHasCounterparts] = useState(false);
  const [autoSwitchMessage, setAutoSwitchMessage] = useState<string | null>(null);

  const [tagSchema, tags, schemaSelectorProps, tagInputsProps] = useCreateBioentityTagInputs();

  const mismatchResult = useSchemaMismatchDetection({
    tagSchemaId: BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS ? (tagSchema?.id ?? null) : null,
    hasCounterparts,
    registryId: tagSchema?.registryId ?? null,
  });

  const useNewCreatePath = BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS;

  const {interfaceIdsBySchemaId, status: ssiQueryStatus} = useSchemaSSIsByIdQuery(!useNewCreatePath);
  const isSsiQueryBlocked =
    useNewCreatePath &&
    (ssiQueryStatus === QueryResultType.PENDING || ssiQueryStatus === QueryResultType.ERROR);
  const tagSchemaFilterFn = useMemo(() => {
    if (!useNewCreatePath || ssiQueryStatus !== QueryResultType.SUCCESS) {
      return undefined;
    }
    return (schema: {id: string}) => {
      const ssiIds = interfaceIdsBySchemaId[schema.id] ?? [];
      return (
        ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_COMPOUND_ID) ||
        ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID)
      );
    };
  }, [useNewCreatePath, ssiQueryStatus, interfaceIdsBySchemaId]);

  const disabled =
    name.trim().length === 0 ||
    folderId == null ||
    posting ||
    hasBlockingError ||
    isParsing ||
    mismatchResult.status === MismatchStatus.BLOCKED ||
    (useNewCreatePath && tagSchema == null);

  // Auto-switch schema when a mismatch is detected and a paired schema is available.
  // Persist the warning message so it remains visible after the switch resolves the mismatch.
  const autoSwitchSchema =
    mismatchResult.status === MismatchStatus.WARNING ? mismatchResult.autoSwitchSchema : null;
  const autoSwitchWarningMessage =
    mismatchResult.status === MismatchStatus.WARNING ? mismatchResult.message : null;
  const {onChange: onSchemaChange} = schemaSelectorProps;
  useEffect(() => {
    if (
      autoSwitchSchema != null &&
      autoSwitchWarningMessage != null &&
      autoSwitchSchema.id !== tagSchema?.id
    ) {
      onSchemaChange(autoSwitchSchema);
      setAutoSwitchMessage(autoSwitchWarningMessage);
    }
  }, [autoSwitchSchema, autoSwitchWarningMessage, onSchemaChange, tagSchema?.id]);

  // Clear the persisted message when the sketcher value changes (new drawing = fresh state)
  useEffect(() => {
    setAutoSwitchMessage(null);
  }, [sketcherValue]);

  async function createMolecule(): Promise<void> {
    assert(folderId != null, 'Unexpected creating molecule with null folderId');
    setPosting(true);

    try {
      if (useNewCreatePath) {
        assert(tagSchema != null, 'Schema is required when salt form bulk create is enabled');
        const schemaFields = buildSchemaFieldsFromTags(tags);
        const molecule = await withLoadingScreen(
          MoleculeDAO.createMoleculeWithParentLink({
            name,
            folderId,
            schemaId: tagSchema.id,
            chemicalStructureInput: {
              structureFormat: ChemicalFormatType.MOLFILE,
              value: sketcherValue ?? EMPTY_MOLFILE,
            },
            ...(Object.keys(schemaFields).length > 0 ? {schemaFields} : {}),
            ...(studySearchItems.length > 0 ? {studyIds: studySearchItems.map(({id}) => id)} : {}),
          }),
          'Creating molecule...'
        );
        writeFragmentsToCache(getFragmentWrapper('Molecule'), [molecule]);
        if (onMoleculeCreated) {
          onMoleculeCreated(molecule.id, molecule.name);
        } else {
          FileStates.get().openFolderItem(molecule.id);
        }
      } else {
        const createBioentityArgs: CreateBioentityArgs = {
          ...getDefaultCreateBioentityArgs(),
          name,
          folderId,
          tagSchema,
          tags,
          studySearchItems,
        };
        createBioentityArgs.chemicalSketcherValue = sketcherValue;
        const shouldStripSalts =
          !BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && sketcherValue != null;
        if (tagSchema != null) {
          await withLoadingScreen(createBioentity(createBioentityArgs), 'Creating molecule...').then(() =>
            shouldStripSalts
              ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
              : Promise.resolve()
          );
        } else {
          await withLoadingScreen(
            MoleculeActions.createMolecule(
              name,
              folderId,
              ChemicalFormatType.MOLFILE,
              sketcherValue ?? EMPTY_MOLFILE,
              null,
              studySearchItems.map(({id}) => id)
            ).then(() =>
              shouldStripSalts
                ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
                : Promise.resolve()
            ),
            'Creating molecule...'
          );
        }
      }
      FolderDAOCache.invalidateFolderContents(folderId);
      onRequestSubmit();
    } catch (err) {
      setPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  }

  return (
    <ExpandableModal
      title="Create molecule"
      buttons={[{disabled, submit: true, text: 'Create'}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={createMolecule}
      startMaximized={true}
      analyticsModule={ANALYTICS_MODULE.CHEMISTRY}
      analyticsModalType="create_molecule"
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Entity name" isRequired={true} />
            <input
              autoFocus={true}
              type="text"
              value={name}
              placeholder="Name"
              onChange={(e) => {
                setName(e.target.value);
              }}
              {...getTestProps({component: 'CreateMoleculeModal', element: 'input', key: 'entityName'})}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              buttonSize={ButtonSize.MEDIUM}
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && (
          <Col sm={6}>
            <FormGroup>
              <FieldLabel label="Studies" />
              <StudySearchInput
                onChange={(studySearchItems: Array<StudySearchItem>) => {
                  setStudySearchItems(studySearchItems);
                }}
                shouldAllowMultiple={true}
                selectedItems={studySearchItems}
              />
            </FormGroup>
          </Col>
        )}
      </Row>
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel
              label="Schema"
              isRequired={useNewCreatePath}
              dataTestAttrs={{component: 'CreateMoleculeModal', element: 'schemaLabel'}}
            />
            <TagSchemaSelector
              allowNone={!useNewCreatePath}
              isDisabled={isSsiQueryBlocked}
              oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
              tagSchemaFilterFn={tagSchemaFilterFn}
              {...schemaSelectorProps}
            />
          </FormGroup>
          <FormGroup>
            <CreateBioentityTagInputs {...tagInputsProps} />
          </FormGroup>
        </Col>
      </Row>
      {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
        <>
          <SchemaMismatchAlert mismatchResult={mismatchResult} autoSwitchMessage={autoSwitchMessage} />
          <ChemicalCounterpartFields
            sketcherValue={sketcherValue}
            schemaId={tagSchema?.id}
            entityName={name.trim() || undefined}
            setHasBlockingError={setHasBlockingError}
            setIsParsing={setIsParsing}
            onHasCounterpartsChange={setHasCounterparts}
          />
        </>
      )}
      <ChemicalStructureModalInput
        initialSketcherValue={initialChemicalStructure?.value ?? EMPTY_MOLFILE}
        onSketcherValueChange={setSketcherValue}
        mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
        inputFormat={
          initialChemicalStructure
            ? (CHEMICAL_FORMAT_TO_MARVIN[initialChemicalStructure.format] ?? null)
            : null
        }
        outputFormat={MarvinChemicalFormat.MOLV3}
      />
    </ExpandableModal>
  );
}

function buildSchemaFieldsFromTags(tags: Array<SchemaTag>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const tag of tags) {
    const key = relevantKeyFromFieldType(tag.tagSchemaField.fieldType);
    if (key) {
      const value = tag[key];
      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
        continue;
      }
      if (getLinksKeyFromFieldType(tag.tagSchemaField.fieldType) != null && Array.isArray(value)) {
        result[tag.tagSchemaField.systemName] = value.map((item: {id: string}) => item.id);
      } else {
        result[tag.tagSchemaField.systemName] = value;
      }
    }
  }
  return result;
}
